// Pure helpers for merging expense/income lines, and for splitting an already
// merged line. Kept free of util-formaters imports so they stay unit-testable
// under vitest (see split-utils for why).
//
// These lines carry tax: Amount is pre-tax per unit, TaxAmount is the
// multiplier, TotalAmount is post-tax for the whole line. Merging collapses
// everything into ONE untaxed line whose Amount is the summed POST-tax total, so
// the invoice total is preserved exactly. The pre-tax subtotal necessarily
// changes -- it becomes the post-tax total -- which is inherent to dropping the
// tax breakdown, and is why the merged line carries no TaxID.

import {buildSplitLabel, centsToMoney, computeSplitAmounts, mergeDescriptions, toCents} from './split-utils'

/** Lines already paid are locked; they are passed through untouched. */
const isPayed = (line) => !!line?.IsPayed

/** Clear the DB row id so a reshaped line is created rather than updating the original. */
function clearedRowIds(line) {
    const cleared = {}

    if ('ExpenseLineID' in line) {
        cleared.ExpenseLineID = ''
    }

    if ('InvoiceLineID' in line) {
        cleared.InvoiceLineID = ''
    }

    return cleared
}

/**
 * Collapse every unpaid line into a single untaxed line, preserving the post-tax
 * total exactly. Paid lines are left exactly as they are and come out first.
 *
 * @param {object[]} lines - plain line shapes
 * @returns {object[]} the paid lines, then at most one merged line
 */
export function mergeLines(lines = []) {
    const payed = lines.filter(isPayed)
    const mergeable = lines.filter(line => !isPayed(line))

    if (!mergeable.length) {
        return [...payed]
    }

    const total = centsToMoney(mergeable.reduce((sum, it) => sum + toCents(it.TotalAmount), 0))

    return [...payed, {
        ...mergeable[0],
        Description: mergeDescriptions(mergeable.map(it => it.Description)),
        Amount: total,
        Qty: 1,
        TaxID: '',
        TaxAmount: 1,
        TotalAmount: total,
        ...clearedRowIds(mergeable[0])
    }]
}

/**
 * Split one merged line into `count` parts that sum back to it exactly. The
 * merged line is untaxed, so Amount and TotalAmount stay equal on every part.
 *
 * @param {object} line - a plain line shape
 * @param {number} count - number of parts
 * @returns {object[]}
 */
export function splitMergedLine(line, count) {
    const parts = Math.max(1, Math.floor(Number(count)) || 1)
    const description = (line?.Description ?? '').toString().trim()

    return computeSplitAmounts(line?.TotalAmount, parts).map((amount, index) => ({
        ...line,
        Description: [description, `(${buildSplitLabel(index + 1, parts)})`].filter(it => !!it).join(' '),
        Amount: amount,
        Qty: 1,
        TaxID: '',
        TaxAmount: 1,
        TotalAmount: amount,
        ...clearedRowIds(line)
    }))
}
